"use client";

import { useEffect, useSyncExternalStore } from "react";
import { indexPieces } from "@/lib/avatar/pieces";

// Les pièces ajoutées sans redéploiement, partagées par tous les avatars de la page.
//
// Pour ne rien charger inutilement :
// - rien n'est demandé tant qu'aucun avatar à l'écran ne porte une de ces pièces ;
// - une seule requête par page, quel que soit le nombre d'avatars ;
// - la dernière liste connue est gardée pour l'onglet (sessionStorage) : en changeant de
//   page, les pièces sont dessinées tout de suite, sans attendre le réseau, puis la liste
//   est revérifiée une fois en arrière-plan.
// Tant qu'elles ne sont pas là (ou si la lecture échoue), la valeur est null : un avatar
// qui porte une de ces pièces s'affiche sans elle.
const STORAGE_KEY = "believemy-avatar-pieces";

let pieces = null;
let loading = null;
const listeners = new Set();

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Le bloc relu de l'onglet repasse par la même vérification que celui du réseau.
function remembered() {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
        return Array.isArray(stored) ? indexPieces(stored) : null;
    } catch {
        return null;
    }
}
if (typeof window !== "undefined") pieces = remembered();

function publish(list) {
    const next = indexPieces(list);
    // Même liste qu'avant : on garde le même objet, aucun avatar n'a besoin de se redessiner.
    if (pieces && JSON.stringify(Object.values(pieces)) === JSON.stringify(Object.values(next))) return;
    pieces = next;
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.values(next)));
    } catch {}
    listeners.forEach((listener) => listener());
}

export function loadPieces({ fresh = false } = {}) {
    if (loading && !fresh) return loading;
    loading = fetch("/api/avatar/pieces", fresh ? { cache: "no-store" } : undefined)
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
        .then((data) => publish(data.pieces))
        .catch(() => {
            // Une lecture ratée pourra être retentée par le prochain avatar qui en a besoin.
            loading = null;
        });
    return loading;
}

// `needed` : faux tant que rien à l'écran n'a besoin des pièces, pour ne pas les charger
// sur les pages où tous les avatars ne portent que des pièces de base.
export default function usePieces(needed = true) {
    const current = useSyncExternalStore(subscribe, () => pieces, () => null);
    useEffect(() => {
        if (needed) loadPieces();
    }, [needed]);
    return current;
}
